import { useDragDropManager, useDragLayer, useDrop, XYCoord } from 'react-dnd';
import { StyleSheetManager } from 'styled-components';
import { RefObject, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Plate } from '@udecode/plate-core';
import { IFrame, useIFrame } from '../Iframe';
import { IframeContentOverlay } from '../IframeContentOverlay';
import { ProviderPropsContext } from '../../context';
import { OverlayContext } from '../../overlay/context';
import { Overlays } from '../../overlay';
import { DEFAULT_NAVBAR_HEIGHT } from '../../constants';
import { CanvasContext, CanvasProps } from './context';
import { useDreamEditorRef } from '../..';
import { useLoadFonts } from '../../utils/useLoadFonts';

function SetupCanvasDnD() {
  const { window: canvasWindow } = useIFrame();
  const manager = useDragDropManager();

  useEffect(() => {
    if (canvasWindow) {
      // using any because here we are using private instance methods of backend
      // see https://github.com/react-dnd/react-dnd/issues/1496#issuecomment-518936256
      const backend: any = manager.getBackend();
      backend.addEventListeners(canvasWindow);
      return () => backend.removeEventListeners(canvasWindow);
    }

    return undefined;
  }, [canvasWindow, manager]);

  return null;
}

/*
    ScrollArea allows dragging over the text editor area to
    start scroll top or bottom
*/
function ScrollArea({
  position,
  iframeRef,
}: {
  position: 'top' | 'bottom';
  iframeRef: RefObject<HTMLIFrameElement>;
}) {
  const START_SCROLL_TOP_THRESHOLD = DEFAULT_NAVBAR_HEIGHT;
  const START_SCROLL_THRESHOLD = 100;

  const { isDragging } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
  }));

  const intensityRef = useRef<number | null>(0);

  const [{ isOver }, drop] = useDrop({
    accept: 'block',
    canDrop: () => true,
    drop: () => {},
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
    hover(item, monitor) {
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y;
      const thresholdTop = START_SCROLL_TOP_THRESHOLD * 2;
      const thresholdBot =
        (iframeRef.current?.contentWindow?.innerHeight || 0) - START_SCROLL_THRESHOLD;

      // Dragging upwards
      if (position === 'top' && hoverClientY <= thresholdTop) {
        const intensity = thresholdTop - hoverClientY;
        intensityRef.current = -intensity;
      }

      if (position === 'bottom' && hoverClientY >= thresholdBot) {
        const intensity = hoverClientY - thresholdBot;
        intensityRef.current = intensity;
      }
    },
  });

  useEffect(() => {
    if (isOver) {
      // FIXME: the scrolling logic isn't smooth in this scenario.
      // We should use a better way to do this.
      const scrollIntervalId = setInterval(() => {
        iframeRef.current?.contentWindow?.scrollBy(0, intensityRef.current || 0);
      }, 100);
      return () => clearInterval(scrollIntervalId);
    }
    return () => {};
  }, [isOver]);

  if (isDragging) {
    return (
      <div
        ref={drop}
        style={{
          height: START_SCROLL_THRESHOLD,
          top: position === 'top' ? 0 : undefined,
          bottom: position === 'bottom' ? 0 : undefined,
          position: 'fixed',
          width: '100%',
          background: 'none',
          transition: 'all 0.5s',
          zIndex: 1,
        }}
      />
    );
  }

  return null;
}

export function Canvas(props: CanvasProps) {
  const { children, theme, isXrayMode, scale } = props;
  const { id, readOnly } = useContext(ProviderPropsContext);

  const { setElement } = useContext(OverlayContext);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  // rerender after iframe is loaded,
  // so that StylesheetManager target prop gets correct value
  const [iFrameLoaded, setIsIframeLoaded] = useState(false);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (iFrameLoaded && iframe && theme) {
      iframe.contentDocument?.documentElement?.setAttribute('data-theme', theme);
    } else {
      iframe?.contentDocument?.documentElement?.removeAttribute('data-theme');
    }

    if (iFrameLoaded && iframe && isXrayMode) {
      iframe.contentDocument?.documentElement?.setAttribute('data-xray-mode', 'true');
    } else {
      iframe?.contentDocument?.documentElement?.removeAttribute('data-xray-mode');
    }
  }, [theme, isXrayMode, iFrameLoaded]);

  const editor = useDreamEditorRef();
  useLoadFonts(editor, iframeRef.current ?? undefined);

  const contextValue = useMemo(
    () => ({
      theme,
      isXrayMode,
      scale,
    }),
    [theme, isXrayMode, scale]
  );

  return (
    <CanvasContext.Provider value={contextValue}>
      <IFrame
        ref={iframeRef}
        title="canvas"
        src="/canvas/index.html"
        style={{
          transform: `scale(${scale || 1})`,
          width: `calc(100%/${scale || 1})`,
          height: `calc(100%/${scale || 1})`,
          transformOrigin: '0px 0px',
        }}
        onLoad={() => setIsIframeLoaded(true)}
      >
        <ScrollArea position="top" iframeRef={iframeRef} />
        <SetupCanvasDnD />
        <StyleSheetManager target={iframeRef.current?.contentDocument?.head}>
          <Plate
            id={id}
            editableProps={{
              readOnly,
              className: 'typedream content',
              style: {
                minHeight: '100%',
                background: 'var(--background)',
              },
            }}
          />
        </StyleSheetManager>
        <ScrollArea position="bottom" iframeRef={iframeRef} />
      </IFrame>
      <IframeContentOverlay ref={setElement} iframeRef={iframeRef}>
        <Overlays>{children}</Overlays>
      </IframeContentOverlay>
    </CanvasContext.Provider>
  );
}

Canvas.defaultProps = {
  theme: undefined,
  isXrayMode: false,
  scale: 1,
  children: undefined,
};
